// "Bridge didn't recognize" flags (ITER-172) — keyed like the songLinks library, server-written.
// Reads go straight to Firestore (DJs only); every write goes through a callable.

import { collection, documentId, getDocs, query, where, type DocumentData } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { isValidSongLinkKey, songLinkKey, type LinkProvider } from '../utils/songLinks';
import { librarySongOf } from './songMatchMemory';
import type { SongRequest } from './requests';

const IN_QUERY_CHUNK = 30;
const LINK_PROVIDERS: LinkProvider[] = ['spotify', 'tidal', 'apple'];

export interface ProviderRecognition {
  flagged: boolean;
  flagCount: number;
  cleanWhileFlagged: number;
  flaggedAt: string | null;
  flaggedBy: string | null;
}

export interface SongRecognition {
  songKey: string;
  title: string;
  artist: string;
  providers: Partial<Record<LinkProvider, ProviderRecognition>>;
}

export type SetRecognitionFlagInput =
  | { requestId: string; flagged: boolean; source: 'manual' | 'off_track' | 'prompt'; heard?: { title: string; artist: string } }
  | { songKey: string; provider: LinkProvider; flagged: false; source: 'library' };

export interface SetRecognitionFlagResult {
  songKey: string;
  provider: LinkProvider;
  flagged: boolean;
  flagCount: number;
  learned?: boolean;
}

function countOf(value: unknown): number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : 0;
}

function isoOrNull(value: unknown): string | null {
  if (value && typeof (value as { toDate?: unknown }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate().toISOString();
  }
  return typeof value === 'string' ? value : null;
}

function docToProviderRecognition(raw: unknown): ProviderRecognition | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as DocumentData;
  return {
    flagged: value.flagged === true,
    flagCount: countOf(value.flagCount),
    cleanWhileFlagged: countOf(value.cleanWhileFlagged),
    flaggedAt: isoOrNull(value.flaggedAt),
    flaggedBy: typeof value.flaggedBy === 'string' ? value.flaggedBy : null,
  };
}

export function docToSongRecognition(songKey: string, data: DocumentData): SongRecognition {
  const rawProviders = (data.providers ?? {}) as DocumentData;
  const providers: SongRecognition['providers'] = {};
  for (const provider of LINK_PROVIDERS) {
    const recognition = docToProviderRecognition(rawProviders[provider]);
    if (recognition) providers[provider] = recognition;
  }
  return {
    songKey,
    title: typeof data.title === 'string' ? data.title : '',
    artist: typeof data.artist === 'string' ? data.artist : '',
    providers,
  };
}

export function flagFor(recognition: SongRecognition | undefined, provider: LinkProvider): ProviderRecognition | null {
  const value = recognition?.providers[provider];
  return value?.flagged ? value : null;
}

export async function recognitionKeyOf(request: Pick<SongRequest, 'linkLibraryKey' | 'song'>): Promise<string> {
  if (isValidSongLinkKey(request.linkLibraryKey)) return request.linkLibraryKey;
  return songLinkKey(librarySongOf(request.song));
}

function chunksOf<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let start = 0; start < items.length; start += size) chunks.push(items.slice(start, start + size));
  return chunks;
}

export async function getSongRecognitions(keys: string[]): Promise<Map<string, SongRecognition>> {
  const validKeys = [...new Set(keys)].filter(isValidSongLinkKey);
  const recognitions = new Map<string, SongRecognition>();
  const snapshots = await Promise.all(chunksOf(validKeys, IN_QUERY_CHUNK).map(chunk =>
    getDocs(query(collection(db, 'songRecognition'), where(documentId(), 'in', chunk)))));
  for (const snapshot of snapshots) {
    for (const docSnap of snapshot.docs) recognitions.set(docSnap.id, docToSongRecognition(docSnap.id, docSnap.data()));
  }
  return recognitions;
}

export async function setBridgeRecognitionFlag(input: SetRecognitionFlagInput): Promise<SetRecognitionFlagResult> {
  const callable = httpsCallable<SetRecognitionFlagInput, SetRecognitionFlagResult>(functions, 'setBridgeRecognitionFlag');
  const response = await callable(input);
  return response.data;
}
